import React from 'react';
import { useTranslator } from 'tradera/lang/translate';
import { useFormatUtcDateInSwedishTimeZone } from 'tradera/hooks/use-format-utc-date-in-swedish-timezone';
import TimeLeft from '../helpers/time-left';
import { ITEM_STATES } from 'tradera/state/views/view-item/constants';
import { AnimateOnValueChange } from './animate-on-value-change';
import LocalizedPrice from 'tradera/localization/localized-price';
import { Button } from 'tradera/components/atoms/button/button';

type BidDetailsProps = {
    bidCount: number;
    endDate: string;
    hasEnded: boolean;
    isEnding: boolean;
    itemState: ITEM_STATES;
    leadingBidAmount: number;
    onShowBids: () => void;
    openingBid: number;
    finalPrice: number;
    timeLeftText: string;
    isTimeLeftBelowWarningLimit: boolean;
};

export const BidDetails = ({
    bidCount,
    endDate,
    hasEnded,
    isEnding,
    itemState,
    leadingBidAmount,
    onShowBids,
    openingBid,
    timeLeftText,
    isTimeLeftBelowWarningLimit,
    finalPrice
}: BidDetailsProps) => {
    const { t } = useTranslator();
    const formatUtcDateInSwedishTimeZone = useFormatUtcDateInSwedishTimeZone();

    const hasBids = bidCount > 0;
    const amountDetails = getAmountDetails(
        itemState,
        leadingBidAmount,
        openingBid,
        hasBids,
        finalPrice
    );

    const amountTitle = (
        <p className="text-nowrap mb-0 bid-details-amount-title">
            {t(amountDetails.title)}
        </p>
    );
    const amountValue = (
        <p className={`text-nowrap heading-madrid mb-0 bid-details-amount`}>
            <AnimateOnValueChange trigger={bidCount}>
                <LocalizedPrice price={amountDetails.amount} />
            </AnimateOnValueChange>
        </p>
    );
    const timeTitle =
        isEnding || hasEnded ? (
            <p className="text-nowrap mb-0 bid-details-time-title">
                {t('vip_hasEndedTime')}{' '}
                {formatUtcDateInSwedishTimeZone(endDate)}
            </p>
        ) : (
            <p className="text-nowrap mb-0 bid-details-time-title">
                {t('vip_willEndTime')} {formatUtcDateInSwedishTimeZone(endDate)}
            </p>
        );
    const timeValue =
        isEnding || hasEnded ? (
            <p
                className={`heading-london mb-0 ${
                    isEnding ? 'text-danger' : ''
                }`}>
                {hasEnded
                    ? itemState === ITEM_STATES.ENDED_CANCELLED
                        ? t('vip_cancelled')
                        : t('vip_ended')
                    : t('vip_ending')}
            </p>
        ) : (
            <p className="heading-london mb-0">
                <TimeLeft
                    isTimeLeftBelowWarningLimit={isTimeLeftBelowWarningLimit}
                    timeLeftText={timeLeftText}
                />
            </p>
        );
    const bidTitle = (
        <p className="mb-0 bid-details-bids-title">
            {t('vip_bid')}{' '}
            <span>
                (
                <Button
                    theme="link"
                    className="text-lowercase"
                    onClick={(e) => {
                        e?.preventDefault();
                        onShowBids();
                    }}>
                    <u>{t('vip_show')}</u>
                </Button>
                )
            </span>
        </p>
    );
    const bidValue = (
        <p className={`text-nowrap heading-london mb-0`}>
            {bidCount} {t('vip_amount')}
        </p>
    );

    const showBidCount =
        itemState !== ITEM_STATES.ENDED_AUCTION_BIN_BOUGHT_AS_BIN;

    return (
        <section className="bid-details d-flex flex-md-column justify-content-between py-1 pt-md-0 pb-md-2">
            <div className="d-flex flex-column flex-md-row align-items-baseline justify-content-between mb-md-2">
                <div className="mb-1 mb-md-0">{amountTitle}</div>
                <div>{amountValue}</div>
            </div>
            <div
                className={`d-flex flex-column flex-md-row justify-content-between mb-md-2 ${
                    showBidCount ? 'text-center' : 'text-right'
                }`}>
                <div className="mb-1 mb-md-0">{timeTitle}</div>
                <div className="my-auto">{timeValue}</div>
            </div>
            {showBidCount && (
                <div className="d-flex flex-column flex-md-row justify-content-between text-right">
                    <div className="mb-1 mb-md-0">{bidTitle}</div>
                    <div className="my-auto">{bidValue}</div>
                </div>
            )}
        </section>
    );
};

export const getAmountDetails = (
    itemState: ITEM_STATES,
    leadingBid: number,
    openingBid: number,
    hasBids: boolean,
    finalPrice: number
) => {
    if (itemState === ITEM_STATES.ENDED_AUCTION_WITH_WINNING_BID) {
        return {
            title: 'vip_finalPrice',
            amount: finalPrice
        };
    }

    if (finalPrice > 0) {
        return {
            title: 'vip_soldFor',
            amount: finalPrice
        };
    }

    if (hasBids) {
        return {
            title: 'vip_leadingBid',
            amount: leadingBid
        };
    }
    return {
        title: 'vip_openingBid',
        amount: openingBid
    };
};
